// ---------------------------------------------------------------------------
// Intelligence Center (Sales Intelligence, phase 2): the management view of the pipeline, built from what the
// deal engine calculates on every deal. Everyone sees only the deals they may see.
// ---------------------------------------------------------------------------
import { z } from 'zod';
import type { Severity } from './dealEngine';
import type { ForecastAccuracyRow } from './intelAnalytics';

const uuid = z.preprocess((v) => (v === '' ? undefined : v), z.string().uuid().optional());
const month = z.preprocess((v) => (v === '' ? undefined : v), z.string().regex(/^\d{4}-\d{2}$/).optional());
export const intelCenterFilterSchema = z.object({
  ownerId: uuid,
  presalesUserId: uuid,
  accountId: uuid,
  consultantId: uuid,
  stageId: uuid,
  risk: z.preprocess((v) => (v === '' ? undefined : v), z.enum(['low', 'medium', 'high', 'critical']).optional()),
  poMonth: month,
  minValue: z.preprocess((v) => (v === '' || v == null ? undefined : Number(v)), z.number().min(0).optional()),
  minCompleteness: z.preprocess((v) => (v === '' || v == null ? undefined : Number(v)), z.number().min(0).max(100).optional()),
  minGp: z.preprocess((v) => (v === '' || v == null ? undefined : Number(v)), z.number().min(-100).max(100).optional()),
  minConfidence: z.preprocess((v) => (v === '' || v == null ? undefined : Number(v)), z.number().min(0).max(100).optional()),
  brand: z.preprocess((v) => (v === '' ? undefined : v), z.string().max(80).optional()),
});
export type IntelCenterFilter = z.infer<typeof intelCenterFilterSchema>;

export interface CenterDealRef { id: string; name: string; accountName: string | null; ownerName: string | null; amount: number | null }
export interface AttentionItem extends CenterDealRef {
  severity: Severity; title: string; description: string; action: string; responsibleName: string | null; rank: number; dueDate: string | null;
}
export interface IntelCenter {
  generatedAt: string;
  brief: string[];
  summary: {
    openDeals: number; totalPipeline: number; weightedPipeline: number; highConfidencePipeline: number; expectedRevenue: number;
    expectedGp: number; commitForecast: number; poThisMonth: number; poThisQuarter: number; avgScore: number | null; avgCompleteness: number | null;
    avgTechReadiness: number | null; avgTenderReadiness: number | null;
  };
  health: Record<Severity, { count: number; value: number }>;
  attention: AttentionItem[];
  po: {
    thisWeek: (CenterDealRef & { date: string; confidence: number | null })[];
    thisMonth: { count: number; value: number };
    nextMonth: { count: number; value: number };
    overdue: (CenterDealRef & { date: string; days: number })[];
    changedLast30: number;
    differences: (CenterDealRef & { salesperson: string; predicted: string; gapDays: number })[];
  };
  tenders: {
    active: number; next7: (CenterDealRef & { deadline: string; quoteReady: boolean; readiness: number | null; risk: string | null })[]; highRisk: number; critical: number; notReady: number;
  };
  presales: { userId: string; name: string; deals: number; value: number; deadlines7: number; overdue: number; highRisk: number; points: number; level: 'low' | 'normal' | 'high' | 'overloaded' }[];
  quotations: { submitted: number; awaitingResponse: number; expiring: (CenterDealRef & { expires: string })[]; avgDiscount: number | null; lowMargin: number; marginErosion: number };
  brands: { key: string; openDeals: number; openValue: number; weighted: number; won: number; wonValue: number; lost: number; winRate: number | null; avgMargin: number | null }[];
  forecast: {
    byMonth: { month: string; value: number; weighted: number; deals: number }[];
    bySalesperson: { userId: string; name: string; pipeline: number; weighted: number; commit: number; deals: number; avgConfidence: number | null }[];
  };
  trend: { day: string; avgScore: number | null; atRisk: number; pipeline: number }[];
  options: {
    owners: { id: string; name: string }[]; presales: { id: string; name: string }[]; accounts: { id: string; name: string }[];
    consultants: { id: string; name: string }[]; stages: { id: string; name: string }[]; brands: string[];
  };
  targetMargin: number;
  /** Pipeline quality: how much of the pipeline can be trusted. */
  quality: Record<'total' | 'weighted' | 'highConfidence' | 'highRisk' | 'lowCompleteness' | 'overduePo' | 'stalled', { count: number; value: number }>;
  completeness: {
    avg: number | null; below50: number; highValueMissingCritical: (CenterDealRef & { score: number | null; missing: string[] })[];
    missing: { key: string; label: string; count: number; value: number }[];
  };
  gp: {
    expected: number; weighted: number; withoutGp: number; atRisk: number;
    bySalesperson: { userId: string; name: string; gp: number; avgPct: number | null; deals: number }[];
    belowTarget: (CenterDealRef & { gpPct: number })[];
    byMonth: { month: string; gp: number; revenue: number }[];
  };
  nba: (CenterDealRef & { actionId: string; /** The deal's salesperson: Assign gives the action to them. */ ownerId: string; action: string; reason: string; priority: Severity; dueDate: string; responsibleName: string | null; management: boolean; status: string })[];
  critical: (CenterDealRef & { stageName: string; gpPct: number | null; poDate: string | null; risk: Severity; completeness: number | null; confidence: number | null; nba: string | null; rank: number })[];
  accuracy: ForecastAccuracyRow | null;
}

export const WORKLOAD_LABEL = { low: 'Low', normal: 'Normal', high: 'High', overloaded: 'Overloaded' } as const;
